/**
 * FaoAutoTrigger — Bandeau admin silencieux pour rappeler la mise à jour FAO.
 *
 * Remplace l'ancien comportement "ouverture forcée du dialog au login"
 * (jugé trop intrusif) par un bandeau jaune discret en haut de page que les
 * admins RH peuvent fermer ou actionner à leur rythme.
 *
 * Logique :
 *   1. Au mount + 1×/jour, GET /api/fao/scraper/check-due
 *   2. Si due=true ET user.role==='hr' → affiche le bandeau
 *   3. Bouton "Mettre à jour" ouvre le FaoScraperDialog (CAPTCHA human-in-the-loop)
 *   4. Bouton × ferme le bandeau pour la journée (localStorage)
 *
 * Skip si :
 *   - API renvoie 401/403 (non-admin) → silencieux
 *   - Dismiss localStorage du jour présent
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import FaoScraperDialog from './FaoScraperDialog';
import { AlertTriangle, X, RefreshCw } from '../lib/icons';

const API = process.env.REACT_APP_BACKEND_URL;
const DISMISS_KEY = 'fao_scraper_banner_dismissed_at';

function _daysAgo(iso) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  return Math.floor(ms / 86_400_000);
}

const FaoAutoTrigger = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [mode, setMode] = useState('weekly');
  const [banner, setBanner] = useState(null);  // { reason, days, last_at }
  const { user } = useAuth();

  useEffect(() => {
    if (!user || user.role !== 'hr') { setBanner(null); return; }
    // Skip si dismiss du jour
    try {
      const d = localStorage.getItem(DISMISS_KEY);
      if (d && new Date(d).toDateString() === new Date().toDateString()) return;
    } catch (e) { /* ignore */ }

    let cancel = false;
    axios.get(`${API}/api/fao/scraper/check-due`)
      .then((r) => {
        if (cancel) return;
        if (!r.data?.due) return;
        setBanner({
          reason: r.data.reason,
          last_at: r.data.last_scrape_at,
          days: _daysAgo(r.data.last_scrape_at),
        });
        setMode(r.data.suggested_mode || 'weekly');
      })
      .catch(() => { /* 401/403 → silencieux */ });
    return () => { cancel = true; };
  }, [user]);

  const handleDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, new Date().toISOString()); } catch (e) { /* ignore */ }
    setBanner(null);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setBanner(null);   // Si l'utilisateur a lancé le scrape, on ne reproposera pas
  };

  return (
    <>
      {banner && (
        <div
          className="fixed top-2 left-1/2 -translate-x-1/2 z-[9999] flex items-center gap-3 px-4 py-2.5 rounded-full shadow-lg border border-amber-200 bg-amber-50 text-amber-900 text-sm max-w-[92vw]"
          data-testid="fao-stale-banner"
          style={{ backdropFilter: 'blur(10px)' }}
        >
          <AlertTriangle className="w-4 h-4 flex-shrink-0 text-amber-600" />
          <span className="truncate">
            {banner.reason === 'no-bulk'
              ? 'FAO : import initial requis'
              : `Données FAO non mises à jour ${banner.days !== null ? `depuis ${banner.days} jour${banner.days > 1 ? 's' : ''}` : 'récemment'}`}
          </span>
          <button
            type="button"
            onClick={() => setDialogOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-600 text-white font-medium hover:bg-amber-700 active:scale-95 transition"
            data-testid="fao-stale-banner-update"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Mettre à jour
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            className="p-1 rounded-full hover:bg-amber-100 active:scale-95 transition"
            data-testid="fao-stale-banner-dismiss"
            aria-label="Fermer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      <FaoScraperDialog open={dialogOpen} mode={mode} onClose={handleDialogClose} />
    </>
  );
};

export default FaoAutoTrigger;
